import {
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box,
  CircularProgress
} from '@mui/material';
import { FormProvider, Table, TextField } from '../../../../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { request, toast, BASE_URL } from '../../../../../utils';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from 'axios';

export const OpdPatientLedger = () => {
  const [searchPatientRegisterationDialog, setSearchPatientRegisterationDialog] = useState(false);
  const [patientDetailList, setPatientDetailList] = useState([]);
  const [filteredPatientList, setFilteredPatientList] = useState([]);
  const [uhidInput, setUhidInput] = useState('');
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        from_date: Yup.string().required('From date is required'),
        to_date: Yup.string().required('To date is required')
      })
    ),
    defaultValues: {
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0],
      uhid: '',
      patient_name: '',
      doctor: '',
      sponsor: '',
      guardian_name: '',
      address: ''
    },
    mode: 'all'
  });

  const { handleSubmit, watch, setValue, reset, formState: { errors } } = methods;

  const PATIENT_TABLE_HEADER = [
    { header: 'UHID', accessor: 'uhid' },
    { header: 'OP No', accessor: 'op_no' },
    { header: 'Reg Date', accessor: (row) => formatDate(row?.date) },
    { header: 'Patient Name', accessor: (row) => `${row.name_suffix || ''} ${row.first_name || ''} ${row.last_name || ''}`.trim() },
    { header: 'Mobile No', accessor: 'address.mobile' },
    { header: 'Doctor', accessor: 'consultant.name' },
    { header: 'Sponsor', accessor: 'sponsor.name' },
    { header: 'Address', accessor: 'address.address' }
  ];

  // Search patient by UHID
  const searchPatientHandler = async (uhid = null) => {
    if (typeof uhid !== 'number' && typeof uhid !== 'string') uhid = null;
    setLoading(true);

    if (!uhid) {
      setSearchPatientRegisterationDialog(true);
      setLoading(false);
      return;
    }

    const response = await request('/patient-registeration-search', {
      method: 'GET',
      query: { uhid }
    });

    if (response?.success && response.data?.length > 0) {
      const patientData = response.data[0];
      populatePatientData(patientData);
      toast('Patient details loaded successfully', { variant: 'success' });
    } else {
      toast('Patient not found', { variant: 'error' });
    }
    setLoading(false);
  };

  // Populate form with patient data
  const populatePatientData = (patientData) => {
    setValue('uhid', patientData.uhid || '');
    setValue('patient_name', `${patientData.name_suffix || ''} ${patientData.first_name || ''} ${patientData.last_name || ''}`.trim());
    setValue('doctor', patientData.consultant?.name || '');
    setValue('sponsor', patientData.sponsor?.name || '');
    setValue('guardian_name', patientData.guardian_name || '');
    setValue('address', `${patientData.address?.address || ''}, ${patientData.address?.city || ''}, ${patientData.address?.state || ''}`.trim());
    setValue('dob', patientData.dob ? new Date(patientData.dob).toISOString().split('T')[0] : '');
    setValue('gender', patientData.gender || '');
    setValue('mobile', patientData.address?.mobile || '');
    setValue('op_no', patientData.op_no || '');
  };

  // Handle UHID search in dialog
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleUhidSearch();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [uhidInput]);

  const handleUhidSearch = async () => {
    const lowercasedInput = uhidInput.toLowerCase().trim();
    try {
      const query = {};
      if (lowercasedInput) {
        query.search = lowercasedInput;
      }
      const response = await request('/patient-registeration-search', {
        method: 'GET',
        query: query
      });
      if (response.success) {
        const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const filteredData = sortedData.filter(
          (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
        );
        setFilteredPatientList(filteredData);
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setFilteredPatientList([]);
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setFilteredPatientList([]);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  };

  const formatDateForExcel = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return '';
    }
  };

  const calculateAge = (dobString) => {
    if (!dobString) return '';
    try {
      const dob = new Date(dobString);
      if (isNaN(dob.getTime())) return '';
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      return `${age} Years`;
    } catch {
      return '';
    }
  };

  // Fetch report data
  const fetchReportData = async () => {
    const uhid = watch('uhid');
    const fromDate = watch('from_date');
    const toDate = watch('to_date');

    if (!uhid) {
      toast('Please select a patient first', { variant: 'error' });
      return null;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/print/hospital/outpatient-management/opd-patient-ledger`, {
        params: {
          refId: JSON.stringify({
            uhid: uhid,
            from_date: fromDate,
            to_date: toDate
          })
        }
      });

      if (response.data.success) {
        setReportData(response.data.data);
        return response.data.data;
      } else {
        toast('No data found for the selected criteria', { variant: 'warning' });
        return null;
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast('Error fetching report data', { variant: 'error' });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle Excel Report
  // Enhanced Excel report generation function
  const handleExcelReport = async () => {
    const data = await fetchReportData();
    if (!data || data.length === 0) return;

    try {
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Format date
      const formatDateForExcel = (dateString) => {
        if (!dateString) return 'N/A';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('en-GB');
        } catch {
          return 'N/A';
        }
      };

      // Calculate age
      const calculateAge = (dobString) => {
        if (!dobString) return '';
        try {
          const dob = new Date(dobString);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age--;
          }
          return `${age} Years`;
        } catch {
          return '';
        }
      };

      // Format currency
      const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 2
        }).format(amount || 0);
      };

      // Process each patient's data
      data.forEach((item, patientIndex) => {
        const patientInfo = item.opd;
        const patientName = `${patientInfo.name_suffix || ''} ${patientInfo.first_name || ''} ${patientInfo.last_name || ''}`.trim();

        // Calculate totals
        let opdTotal = patientInfo.total || 0;
        let totalBillAmount = 0; // Before any discounts
        let totalItemDiscount = 0;
        let totalBillDiscount = 0;
        let totalGST = 0;
        let netBillingAmount = 0; // After all discounts
        let allBills = [];

        // Process billing details - Group by bill
        if (item.billing_details && item.billing_details.length > 0) {
          const billMap = {};

          // Group service items by bill
          item.billing_details.forEach(bill => {
            const billId = bill._id;

            if (!billMap[billId]) {
              // Calculate bill totals
              const billNetAmount = bill.net_amt || 0;
              const billTotalAmount = bill.total_amt || 0;
              const billDiscount = bill.disc_amt || 0;
              const billGST = bill.gst_amt || 0;

              billMap[billId] = {
                billNo: bill.bill_no,
                date: bill.date,
                time: bill.time,
                totalAmt: billTotalAmount,
                billDiscAmt: billDiscount,
                gstAmt: billGST,
                netAmt: billNetAmount,
                totalQty: bill.total_qty || 0,
                payMode: bill.pay_mode || '',
                serviceItems: []
              };

              // Add to totals
              totalBillAmount += billTotalAmount;
              netBillingAmount += billNetAmount;
              totalBillDiscount += billDiscount;
              totalGST += billGST;
            }

            // Check if service_items is an object
            if (bill.service_items) {
              const service = bill.service_items;
              const amount = (service.rate || 0) * (service.quantity || 1);
              const itemDiscount = service.disc_amt || 0;

              billMap[billId].serviceItems.push({
                serviceName: service.service_name,
                doctor: service.doctor,
                quantity: service.quantity || 1,
                rate: service.rate || 0,
                amount: amount,
                itemDiscount: itemDiscount,
                gst: service.gst_amt || 0,
                netAmount: service.net_amt || 0
              });

              totalItemDiscount += itemDiscount;
            }
          });

          // Convert bill map to array
          allBills = Object.values(billMap);
        }

        const grandTotal = opdTotal + netBillingAmount;

        // Create worksheet for this patient
        const wsData = [];

        // =============== HEADER SECTION ===============
        wsData.push(['HOSPITAL MANAGEMENT SYSTEM']);
        wsData.push(['OPD PATIENT LEDGER REPORT']);
        wsData.push(['']);
        wsData.push(['Report Period:', `${formatDateForExcel(watch('from_date'))} to ${formatDateForExcel(watch('to_date'))}`, '', '', '', '', 'Generated:', new Date().toLocaleDateString()]);
        wsData.push(['']);

        // =============== PATIENT INFORMATION ===============
        wsData.push(['PATIENT INFORMATION']);
        wsData.push(['───────────────', '', '', '', '', '', '───────────────']);
        wsData.push(['']);
        wsData.push([
          'UHID:', patientInfo.uhid,
          'OP No:', patientInfo.op_no,
          'Registration Date:', formatDateForExcel(patientInfo.date)
        ]);
        wsData.push([
          'Patient Name:', patientName,
          'Gender:', patientInfo.gender,
          'Age:', calculateAge(patientInfo.dob)
        ]);
        wsData.push([
          'Guardian:', patientInfo.guardian_name || 'N/A',
          'Mobile:', patientInfo.address?.mobile || 'N/A',
          'Sponsor:', item.name || 'N/A'
        ]);
        wsData.push([
          'Address:', patientInfo?.address?.address || 'N/A',
          '', '',
          'Doctor:', patientInfo.consultant?.name || 'N/A'
        ]);
        wsData.push(['']);

        // =============== OPD REGISTRATION ===============
        if (patientInfo.reg || patientInfo.total) {
          wsData.push(['OPD REGISTRATION']);
          wsData.push(['───────────────', '', '', '', '', '', '───────────────']);
          wsData.push(['']);

          wsData.push([
            'Date', 'OP No', 'Service', 'Registration', 'Discount', 'Total'
          ]);

          wsData.push([
            formatDateForExcel(patientInfo.date),
            patientInfo.op_no,
            'OPD Registration & Consultation',
            patientInfo.reg || 0,
            patientInfo.disc || 0,
            patientInfo.total || 0
          ]);
          wsData.push(['']);
        }

        // =============== OPD BILLING DETAILS ===============
        if (allBills.length > 0) {
          wsData.push(['OPD BILLING DETAILS']);
          wsData.push(['───────────────', '', '', '', '', '', '', '', '', '', '', '───────────────']);
          wsData.push(['']);

          // Process each bill
          allBills.forEach((bill, billIndex) => {
            // Bill Header
            wsData.push([
              `Bill No: ${bill.billNo}`,
              `Date: ${formatDateForExcel(bill.date)}${bill.time ? ` Time: ${bill.time}` : ''}`,
              `Payment Mode: ${bill.payMode}`,
              `Total Items: ${bill.totalQty}`
            ]);
            wsData.push(['']);

            // Service Items Header
            wsData.push([
              'Sr.No', 'Service Description', 'Qty', 'Rate', 'Amount',
              'Item Disc', 'GST', 'Net Amount'
            ]);

            // Service Items
            bill.serviceItems.forEach((item, itemIndex) => {
              wsData.push([
                itemIndex + 1,
                item.serviceName,
                item.quantity,
                item.rate,
                item.amount,
                item.itemDiscount,
                item.gst,
                item.netAmount
              ]);
            });

            // Bill Summary
            wsData.push(['']);
            wsData.push([
              'Bill Summary:', '',
              'Total Amount:', bill.totalAmt,
              'Bill Discount:', bill.billDiscAmt,
              'GST:', bill.gstAmt,
              'Bill Net Total:', bill.netAmt
            ]);
            wsData.push(['']);
            wsData.push(['']);
          });
        } else {
          wsData.push(['OPD BILLING DETAILS']);
          wsData.push(['───────────────']);
          wsData.push(['']);
          wsData.push(['No billing records found for this period']);
          wsData.push(['']);
        }

        // =============== FINANCIAL SUMMARY ===============
        wsData.push(['FINANCIAL SUMMARY']);
        wsData.push(['───────────────', '', '', '', '', '', '───────────────']);
        wsData.push(['']);

        // Summary rows
        wsData.push(['Category', 'Amount']);
        wsData.push(['OPD Registration Total:', opdTotal]);
        wsData.push(['Total Bill Amount (Before Discounts):', totalBillAmount]);
        wsData.push(['Total Item Discounts:', totalItemDiscount]);
        wsData.push(['Total Bill Discounts:', totalBillDiscount]);
        wsData.push(['Total GST:', totalGST]);
        wsData.push(['Net Billing Amount:', netBillingAmount]);
        wsData.push(['───────────────', '───────────────']);
        wsData.push(['GRAND TOTAL (OPD + Billing):', grandTotal]);

        wsData.push(['']);
        wsData.push(['']);
        wsData.push(['']);

        // Footer
        wsData.push(['', '', '', '', '', '', '', '']);
        wsData.push(['Prepared By: ________________', '', '', '', 'Authorized Signatory: ________________']);
        wsData.push(['Date: ________________', '', '', '', 'Stamp & Seal']);

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // =============== STYLING AND FORMATTING ===============

        // Define column widths
        const wscols = [
          { wch: 10 },  // Column A
          { wch: 25 },  // Column B
          { wch: 15 },  // Column C
          { wch: 15 },  // Column D
          { wch: 15 },  // Column E
          { wch: 15 },  // Column F
          { wch: 15 },  // Column G
          { wch: 15 },  // Column H
        ];
        ws['!cols'] = wscols;

        // Define range for styling
        const range = XLSX.utils.decode_range(ws['!ref']);

        // Apply styling to cells
        for (let R = range.s.r; R <= range.e.r; ++R) {
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell_address = { c: C, r: R };
            const cell_ref = XLSX.utils.encode_cell(cell_address);

            if (!ws[cell_ref]) continue;

            // Initialize cell style if not exists
            if (!ws[cell_ref].s) {
              ws[cell_ref].s = {};
            }

            // Main headers
            if (R === 0) { // Hospital Name
              ws[cell_ref].s = {
                font: { bold: true, sz: 16, color: { rgb: "1976D2" } },
                alignment: { horizontal: 'center' }
              };
            }

            if (R === 1) { // Report Title
              ws[cell_ref].s = {
                font: { bold: true, sz: 14 },
                alignment: { horizontal: 'center' }
              };
            }

            // Section headers
            if (ws[cell_ref].v === 'PATIENT INFORMATION' ||
              ws[cell_ref].v === 'OPD REGISTRATION' ||
              ws[cell_ref].v === 'OPD BILLING DETAILS' ||
              ws[cell_ref].v === 'FINANCIAL SUMMARY') {
              ws[cell_ref].s = {
                font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "1976D2" } },
                alignment: { horizontal: 'center' }
              };
            }

            // Table headers
            if ((R === 16 && C <= 7) || // Billing headers
              (R === 8 && C <= 5) ||  // OPD Registration headers
              (R === 10 && C <= 5)) { // Financial summary headers
              if (typeof ws[cell_ref].v === 'string' && ws[cell_ref].v !== '') {
                ws[cell_ref].s = {
                  font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
                  fill: { fgColor: { rgb: "2C3E50" } },
                  alignment: { horizontal: 'center' },
                  border: {
                    top: { style: 'thin', color: { rgb: "000000" } },
                    bottom: { style: 'thin', color: { rgb: "000000" } },
                    left: { style: 'thin', color: { rgb: "000000" } },
                    right: { style: 'thin', color: { rgb: "000000" } }
                  }
                };
              }
            }

            // Bill headers
            if (typeof ws[cell_ref].v === 'string' && ws[cell_ref].v.includes('Bill No:')) {
              ws[cell_ref].s = {
                font: { bold: true, sz: 11, color: { rgb: "000000" } },
                fill: { fgColor: { rgb: "E8F4FD" } }
              };
            }

            // Bill summary rows
            if (typeof ws[cell_ref].v === 'string' && ws[cell_ref].v === 'Bill Summary:') {
              ws[cell_ref].s = {
                font: { bold: true, sz: 10 },
                fill: { fgColor: { rgb: "F0F7FF" } }
              };
            }

            // Grand Total row
            if (typeof ws[cell_ref].v === 'string' && ws[cell_ref].v === 'GRAND TOTAL (OPD + Billing):') {
              ws[cell_ref].s = {
                font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "27AE60" } }
              };
              // Style the amount cell next to it
              const amountCell = XLSX.utils.encode_cell({ c: C + 1, r: R });
              if (ws[amountCell]) {
                ws[amountCell].s = {
                  font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
                  fill: { fgColor: { rgb: "27AE60" } },
                  numFmt: '#,##0.00'
                };
              }
            }

            // Numeric cells (apply number format)
            if (typeof ws[cell_ref].v === 'number') {
              ws[cell_ref].z = '#,##0.00';
              ws[cell_ref].s = {
                ...ws[cell_ref].s,
                numFmt: '#,##0.00'
              };

              // Right align numeric cells
              if (!ws[cell_ref].s.alignment) {
                ws[cell_ref].s.alignment = {};
              }
              ws[cell_ref].s.alignment.horizontal = 'right';
            }

            // Patient info labels (bold)
            if (typeof ws[cell_ref].v === 'string' && ws[cell_ref].v.endsWith(':')) {
              ws[cell_ref].s = {
                ...ws[cell_ref].s,
                font: { bold: true }
              };
            }
          }
        }

        // Merge cells for headers
        ws['!merges'] = [
          // Hospital name
          { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
          // Report title
          { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } },
          // Section headers
          { s: { r: 5, c: 0 }, e: { r: 5, c: 7 } },
          { s: { r: 10, c: 0 }, e: { r: 10, c: 7 } },
          { s: { r: 14, c: 0 }, e: { r: 14, c: 7 } },
          { s: { r: 24, c: 0 }, e: { r: 24, c: 7 } },
        ];

        // Add worksheet to workbook
        const sheetName = patientName.substring(0, 31) || `Patient_${patientIndex + 1}`;
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      // Generate filename
      const fileName = `OPD_Patient_Ledger_${watch('uhid') || 'Report'}_${new Date().toISOString().split('T')[0]}`;

      // Save file
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        bookSST: false
      });

      const dataBlob = new Blob([excelBuffer], { type: fileType });
      saveAs(dataBlob, fileName + fileExtension);

      toast('Excel report generated successfully', { variant: 'success' });

    } catch (error) {
      console.error('Error generating Excel report:', error);
      toast('Error generating Excel report', { variant: 'error' });
    }
  };




  // Handle PDF Report
  const handlePdfReport = async () => {
    const data = await fetchReportData();
    if (!data || data.length === 0) return;

    window.open(
      `/print${pathname}?refId=${JSON.stringify({
        uhid: watch('uhid'),
        from_date: watch('from_date'),
        to_date: watch('to_date')
      })}`,
      '_blank'
    );
  };

  return (
    <>
      <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
        <FormProvider methods={methods}>
          <Container sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography color="primary" variant="h4" sx={{ fontWeight: 600 }}>
                OPD Patient Ledger
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handlePdfReport}
                  disabled={!watch('uhid') || loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  View PDF Report
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleExcelReport}
                  disabled={!watch('uhid') || loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  View Excel Report
                </Button>
                <Button variant="outlined" onClick={() => reset()} disabled={loading}>
                  Clear
                </Button>
                <Button variant="contained" color="secondary">
                  Exit
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ border: '1px solid', borderColor: 'grey.300', p: 3, borderRadius: '8px', mb: 3 }}>
              <Grid container spacing={2}>
                {/* UHID Search */}
                <Grid item xs={12} md={5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ width: 100, fontWeight: 600 }}>UHID</Typography>
                    <TextField
                      sx={{ flex: 1 }}
                      name="uhid"
                      disabled={loading}
                    />
                    <Button
                      variant="contained"
                      onClick={() => searchPatientHandler()}
                      disabled={loading}
                      sx={{ minWidth: 40 }}
                    >
                      <SearchIcon />
                    </Button>
                  </Stack>
                </Grid>

                {/* Patient Name */}
                <Grid item xs={12} md={7}>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ width: 120, fontWeight: 600 }}>Patient Name</Typography>
                    <TextField sx={{ flex: 1 }} name="patient_name" disabled />
                  </Stack>
                </Grid>

                {/* From Date */}
                <Grid item xs={12} md={6}>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ width: 100, fontWeight: 600 }}>From Date</Typography>
                    <TextField
                      sx={{ flex: 1 }}
                      name="from_date"
                      type="date"
                      error={!!errors.from_date}
                      helperText={errors.from_date?.message}
                      disabled={loading}
                    />
                  </Stack>
                </Grid>

                {/* To Date */}
                <Grid item xs={12} md={6}>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ width: 100, fontWeight: 600 }}>To Date</Typography>
                    <TextField
                      sx={{ flex: 1 }}
                      name="to_date"
                      type="date"
                      error={!!errors.to_date}
                      helperText={errors.to_date?.message}
                      disabled={loading}
                    />
                  </Stack>
                </Grid>

                {/* Doctor */}
                <Grid item xs={12} md={6}>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ width: 100, fontWeight: 600 }}>Doctor</Typography>
                    <TextField sx={{ flex: 1 }} name="doctor" disabled />
                  </Stack>
                </Grid>

                {/* Sponsor */}
                <Grid item xs={12} md={6}>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ width: 100, fontWeight: 600 }}>Sponsor</Typography>
                    <TextField sx={{ flex: 1 }} name="sponsor" disabled />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </FormProvider>
      </Paper>

      {/* Patient Search Dialog */}
      <Dialog
        open={searchPatientRegisterationDialog}
        onClose={() => setSearchPatientRegisterationDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <Stack p={2} borderBottom="1px solid" borderColor="grey.300">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">Search Patient</Typography>
            <IconButton onClick={() => setSearchPatientRegisterationDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          <MuiTextField
            sx={{ mt: 2 }}
            fullWidth
            label="Search by UHID, Name, Mobile, OP No"
            variant="outlined"
            value={uhidInput}
            onChange={(e) => setUhidInput(e.target.value)}
            autoFocus
          />
        </Stack>

        <Box sx={{ height: 'calc(100% - 120px)', overflow: 'auto' }}>
          <Table
            columns={PATIENT_TABLE_HEADER}
            data={filteredPatientList || []}
            loading={loading}
            onRowClick={(row) => {
              populatePatientData(row);
              setSearchPatientRegisterationDialog(false);
              setUhidInput('');
            }}
            sx={{ height: '100%' }}
          />
        </Box>
      </Dialog>
    </>
  );
};